import { createRoot } from 'react-dom/client'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useProgress } from '@react-three/drei'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Scene from './Scene'
import Overlay from './Overlay/Overlay'
import ProjectModal from './components/ProjectModal'
import ParticlesCursor from './components/ParticlesCursor'
import { startGlobalAudio } from './Overlay/Nav'
import './loader.css'
export let globalLenis = null
gsap.registerPlugin(ScrollTrigger)
function setViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}
setViewportHeight();
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', () => {
  setTimeout(setViewportHeight, 100);
});
const root = createRoot(document.getElementById('root'))
function CustomLoader({ onComplete }) {
  const { progress } = useProgress();
  const [show, setShow] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [finalProgress, setFinalProgress] = useState(0);
  const maxProgressRef = useRef(0);
  if (progress > maxProgressRef.current) {
    maxProgressRef.current = progress;
  }
  const cappedProgress = Math.min(maxProgressRef.current, 99);
  useEffect(() => {
    if (maxProgressRef.current >= 100 && finalProgress < 100) {
      const timer = setTimeout(() => {
        setFinalProgress(100);
      }, 2000); // 2 soniya kutish
      return () => clearTimeout(timer);
    }
  }, [progress, finalProgress]);
  const displayProgress = finalProgress >= 100 ? 100 : cappedProgress;
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      const blockScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener('wheel', blockScroll, { passive: false });
      document.addEventListener('touchmove', blockScroll, { passive: false });
      return () => {
        document.removeEventListener('wheel', blockScroll);
        document.removeEventListener('touchmove', blockScroll);
      };
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [show]);
  useEffect(() => {
    if (finalProgress >= 100) {
      const timer = setTimeout(() => setIsReady(true), 100);
      return () => clearTimeout(timer);
    }
  }, [finalProgress]);
  const handleEnter = useCallback(() => {
    startGlobalAudio();
    setFadeOut(true);
    setTimeout(() => {
      setShow(false);
      onComplete?.();
    }, 800);
  }, [onComplete]);
  useEffect(() => {
    if (!isReady) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleEnter();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReady, handleEnter]);
  if (!show) return null;
  return (
    <div className={`custom-loader ${fadeOut ? 'fade-out' : ''}`}>
      <div className="loader-content">
        {}
        <div className="loader-lines">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="loader-line" />
          ))}
        </div>
        {}
        <div className="loader-progress">
          <div className="loader-bar">
            <div className="loader-fill" style={{ width: `${displayProgress}%` }} />
          </div>
          <span className="loader-percent">{Math.round(displayProgress)}%</span>
        </div>
        {}
        <button
          className={`loader-enter ${isReady ? 'visible' : ''}`}
          onClick={handleEnter}
        >
          <span className="enter-text">Enter</span>
          <span className="enter-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </button>
        {}
        <span className={`loader-hint ${isReady ? 'visible' : ''}`}>
          Press Enter or click to continue
        </span>
      </div>
    </div>
  );
}
function useLenis(enabled = true) {
  const lenisRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    const slowSections = ['.section-5', '.section-6', '.pin-spacer'];
    const fastSections = ['.section-7', '.section-8', '.section-9', '.section-10', '.section-11', '.section-12', '.section-13', '.section-14', '.section-15'];
    const normalMultiplier = 1.2;     
    const slowMultiplier = 0.05;      
    const fastMultiplier = 0.05;      
    const lenis = new Lenis({
      duration: 1.0,             
      easing: (t) => 1 - Math.pow(1 - t, 2), 
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: normalMultiplier,
      touchMultiplier: 2.5,
      infinite: false,
    });
    lenisRef.current = lenis;
    globalLenis = lenis;  // Global reference
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);
    let cachedSlowElements = null;
    let cacheTimeout = null;
    const getCachedSlowElements = () => {
      if (cachedSlowElements === null) {
        cachedSlowElements = [];
        for (const selector of slowSections) {
          const sections = document.querySelectorAll(selector);
          for (const section of sections) {
            cachedSlowElements.push(section);
          }
        }
        clearTimeout(cacheTimeout);
        cacheTimeout = setTimeout(() => {
          cachedSlowElements = null;
        }, 2000);
      }
      return cachedSlowElements;
    };
    let cachedFastElements = null;
    let fastCacheTimeout = null;
    const getCachedFastElements = () => {
      if (cachedFastElements === null) {
        cachedFastElements = [];
        for (const selector of fastSections) {
          const sections = document.querySelectorAll(selector);
          for (const section of sections) {
            cachedFastElements.push(section);
          }
        }
        clearTimeout(fastCacheTimeout);
        fastCacheTimeout = setTimeout(() => {
          cachedFastElements = null;
        }, 2000);
      }
      return cachedFastElements;
    };
    const isInSlowSection = () => {
      const elements = getCachedSlowElements();
      for (const section of elements) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.3) {
          return true;
        }
      }
      return false;
    };
    const isInFastSection = () => {
      const elements = getCachedFastElements();
      for (const section of elements) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.3) {
          return true;
        }
      }
      return false;
    };
    lenis.on('scroll', () => {
      if (isInSlowSection()) {
        lenis.options.wheelMultiplier = slowMultiplier;
        lenis.options.touchMultiplier = 0.5;
      } else if (isInFastSection()) {
        lenis.options.wheelMultiplier = fastMultiplier;
        lenis.options.touchMultiplier = 1.3;  
      } else {
        lenis.options.wheelMultiplier = normalMultiplier;
        lenis.options.touchMultiplier = 2.5;
      }
    });
    const refreshTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(refreshTimeout);
      clearTimeout(resizeTimeout);
      clearTimeout(cacheTimeout);
      clearTimeout(fastCacheTimeout);
      cachedSlowElements = null;
      cachedFastElements = null;
      window.removeEventListener('resize', handleResize);
      gsap.ticker.remove(lenis.raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [enabled]);
  return lenisRef;
}
function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const handleLoaderComplete = () => {
    setIsLoaded(true);
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);
  };
  const lenisRef = useLenis(isLoaded);
  useEffect(() => {
    if (lenisRef.current) {
      if (selectedProjectId) {
        lenisRef.current.stop();
      } else {
        lenisRef.current.start();
      }
    }
  }, [selectedProjectId, lenisRef]);
  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId);
  };
  const handleCloseModal = () => {
    setSelectedProjectId(null);
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  };
  return (
    <>
      {}
      <Scene onProjectClick={handleProjectClick} />
      {}
      <Overlay />
      {}
      <ParticlesCursor />
      {}
      <CustomLoader onComplete={handleLoaderComplete} />
      {}
      <ProjectModal
        projectId={selectedProjectId}
        onClose={handleCloseModal}
      />
    </>
  )
}
root.render(<App />)